import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Checkbox input component with controlled checked state,
 * onCheckedChange callback, and indeterminate state support via DOM ref.
 * Indeterminate is a DOM property (not an HTML attribute) and must be set
 * imperatively on the underlying <input> element.
 */
import * as React from "react";
import { cn } from "./utils.js";
/**
 * Checkbox component with support for onCheckedChange callback and indeterminate state.
 * @param props - CheckboxProps including className, onCheckedChange, onChange, and indeterminate
 * @param ref - forwarded ref to the underlying HTMLInputElement
 */
const Checkbox = React.forwardRef(({ className, onCheckedChange, onChange, indeterminate, ...props }, ref) => {
    const internalRef = React.useRef(null);
    // Expose the underlying input element via the forwarded ref while also
    // keeping our own internal ref so we can set the indeterminate DOM property.
    React.useImperativeHandle(ref, () => internalRef.current);
    // The `indeterminate` property is a DOM-only property -- it has no HTML
    // attribute equivalent and cannot be controlled via JSX. It must be set
    // imperatively on the element whenever the prop value changes.
    React.useEffect(() => {
        if (internalRef.current) {
            internalRef.current.indeterminate = !!indeterminate;
        }
    }, [indeterminate]);
    /**
     * Handles checkbox change events, forwarding to both onChange and onCheckedChange.
     * @param e - the React change event from the underlying input element
     */
    const handleChange = (e) => {
        onChange?.(e);
        onCheckedChange?.(e.target.checked);
    };
    return (_jsx("input", { type: "checkbox", className: cn("h-4 w-4 shrink-0 rounded-sm border border-input ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-primary", className), ref: internalRef, onChange: handleChange, ...props }));
});
Checkbox.displayName = "Checkbox";
export { Checkbox };
//# sourceMappingURL=checkbox.js.map